/**
 * The firm's intake templates for Firm Knowledge: its saved templates
 * (archived ones included, for the Archived group) and the catalog, whose
 * visa starters and form intakes are the CaseBlink Starters. Starter schemas
 * load on demand; the catalog listing is metadata only.
 */
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeSavedTemplate,
  OutgoingIntakeTemplate
} from "@/api/types/outgoingIntake";

import { useOnIntakeTemplatesInvalidated } from "../intakeTemplateEvents";

export type IntakeTemplatesState = {
  saved: OutgoingIntakeSavedTemplate[];
  catalog: OutgoingIntakeTemplate[];
  /** Firm templates that are not archived: the tab's count. */
  activeCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  savedById: (id: string) => OutgoingIntakeSavedTemplate | null;
  catalogById: (id: string) => OutgoingIntakeTemplate | null;
  applySaved: (template: OutgoingIntakeSavedTemplate) => void;
  removeSaved: (templateId: string) => void;
  /** A starter's questions (fetched once). */
  loadStarterSchema: (templateId: string) => Promise<OutgoingIntakeFormNode[]>;
};

export const useIntakeTemplates = (enabled: boolean): IntakeTemplatesState => {
  const [saved, setSaved] = useState<OutgoingIntakeSavedTemplate[]>([]);
  const [catalog, setCatalog] = useState<OutgoingIntakeTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const [firm, all] = await Promise.all([
        OutgoingIntakesAPI.listSavedTemplates({ includeArchived: true }),
        OutgoingIntakesAPI.listTemplates()
      ]);
      if (!alive.current) return;
      setSaved(firm);
      setCatalog((current) =>
        all.map((t) => ({
          ...t,
          schema:
            t.schema ??
            current.find((c) => c.template_id === t.template_id)?.schema
        }))
      );
      setError(null);
    } catch (err) {
      Sentry.captureException(err);
      if (alive.current)
        setError(
          err instanceof Error ? err.message : "Could not load the templates."
        );
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (enabled) void refresh();
    return () => {
      alive.current = false;
    };
  }, [enabled, refresh]);

  useOnIntakeTemplatesInvalidated(() => {
    if (enabled) void refresh();
  });

  const applySaved = useCallback((template: OutgoingIntakeSavedTemplate) => {
    setSaved((current) =>
      current.some((t) => t.template_id === template.template_id)
        ? current.map((t) =>
            t.template_id === template.template_id ? template : t
          )
        : [template, ...current]
    );
  }, []);

  const removeSaved = useCallback((templateId: string) => {
    setSaved((current) => current.filter((t) => t.template_id !== templateId));
  }, []);

  const savedById = useCallback(
    (id: string) => saved.find((t) => t.template_id === id) ?? null,
    [saved]
  );
  const catalogById = useCallback(
    (id: string) => catalog.find((t) => t.template_id === id) ?? null,
    [catalog]
  );

  const loadStarterSchema = useCallback(
    async (templateId: string) => {
      const known = catalog.find((t) => t.template_id === templateId)?.schema;
      if (known) return known;
      const [loaded] = await OutgoingIntakesAPI.listTemplates({
        templateIds: [templateId]
      }).then((all) => all.filter((t) => t.template_id === templateId));
      const schema = loaded?.schema ?? [];
      setCatalog((current) =>
        current.map((t) =>
          t.template_id === templateId ? { ...t, schema } : t
        )
      );
      return schema;
    },
    [catalog]
  );

  const activeCount = useMemo(
    () => saved.filter((t) => !t.archived_at).length,
    [saved]
  );

  return {
    saved,
    catalog,
    activeCount,
    loading,
    error,
    refresh,
    savedById,
    catalogById,
    applySaved,
    removeSaved,
    loadStarterSchema
  };
};
